/**
 * IntentSignupFlow — intent-first "easy signup" (services-expansion).
 *
 * Replaces the legacy account-type chooser when VITE_SIGNUP_INDIVIDUAL_FIRST is
 * on. Flow:
 *   intent → (professional only: roles) → account → email OTP → (professional
 *   only: pro-onboarding = company + activities + licence + Nafath) → done.
 *
 * Reuses existing pieces: useAuth.signUp/verifyRegistration/activateSession,
 * AccountInfoForm, OtpInput, the Nafath service + <NafathVerification>, and the
 * /account/company + /account/activities endpoints. Self-contained local state
 * (no server draft) so the live legacy wizard is untouched as a fallback.
 *
 * The step indicator above every screen and the Back buttons follow the same
 * path rules and history stack (signupPlan.ts).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Navigation from '../../feature/Navigation';
import Footer from '../../feature/Footer';
import Button from '../../base/Button';
import Input from '../../base/Input';
import Stepper from '../../base/Stepper';
import { AccountInfoForm, OtpInput } from '../index';
import NafathVerification, {
  mapApiStatusToNafathStatus,
  type NafathStatus,
} from '../../verification/NafathVerification';
import {
  initiateNafath,
  checkNafathStatus,
  fetchNafathMe,
  type NafathMeResponse,
} from '../../../services/nafathService';
import { useAuth, type StoredSession } from '../../../hooks/useAuth';
import type { AccountInfo } from '../../../hooks/useRegistrationWizard';
import { apiRequest, ApiError } from '../../../utils/apiClient';
import {
  rolesForKind,
  roleLabel,
  roleRequiresLicense,
  type ActivityType,
  type AccountKind,
} from './roleCatalog';
// Screen and Intent (with their meaning) live in signupPlan, next to the path
// rules the step indicator and the Back button share.
import {
  INITIAL_SIGNUP_NAV,
  goBack,
  goTo,
  planFor,
  startAt,
  type SignupIntent as Intent,
  type SignupNav,
  type SignupScreen as Screen,
} from './signupPlan';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

// Brand-consistent card shell.
const CARD = 'rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 sm:p-6 shadow-sm';

export default function IntentSignupFlow({ initialRole }: { initialRole?: ActivityType } = {}) {
  const { t, i18n } = useTranslation('registration');
  const isAr = i18n.language === 'ar';
  const dir = isAr ? 'rtl' : 'ltr';
  const navigate = useNavigate();
  const { signUp, verifyRegistration, activateSession, resendRegistrationOtp } = useAuth();

  // History stack: forward pushes, Back pops (see signupPlan.ts), so Back never
  // lands on a screen this person did not see.
  const [nav, setNav] = useState<SignupNav>(INITIAL_SIGNUP_NAV);
  const { screen } = nav;
  const go = useCallback((next: Screen) => setNav((current) => goTo(current, next)), []);
  const [intent, setIntent] = useState<Intent | null>(null);
  // On the intent screen, "Real-estate marketer" expands to an individual/company
  // sub-choice before continuing.
  const [marketerOpen, setMarketerOpen] = useState(false);
  const [accountKind, setAccountKind] = useState<AccountKind>('individual');
  const [declaredRoles, setDeclaredRoles] = useState<ActivityType[]>([]);
  const [companyName, setCompanyName] = useState('');
  const [companyCr, setCompanyCr] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');

  const [account, setAccount] = useState<AccountInfo>({ firstName: '', lastName: '', email: '', password: '', phone: '' });
  const [accountValid, setAccountValid] = useState(false);

  const [otp, setOtp] = useState('');
  const [pendingSession, setPendingSession] = useState<StoredSession | null>(null);

  const [licenses, setLicenses] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // An error belongs to the screen it was raised on; leaving that screen clears it.
  const back = useCallback(() => {
    setError('');
    setNav((current) => goBack(current));
  }, []);

  const tt = (en: string, ar: string) => (isAr ? ar : en);

  // Preselect a professional role when deep-linked from a "Register with us" nav
  // card (?role=DEVELOPER…). Jumps straight to the roles screen with the right
  // account kind + that role checked; the user can still switch kind or add more.
  const didInitRole = useRef(false);
  useEffect(() => {
    if (didInitRole.current || !initialRole) return;
    didInitRole.current = true;
    const companyOnly: ActivityType[] = ['DEVELOPER', 'PROPERTY_MANAGEMENT', 'FACILITIES_MANAGEMENT'];
    setIntent('professional');
    setAccountKind(companyOnly.includes(initialRole) ? 'company' : 'individual');
    setDeclaredRoles([initialRole]);
    setNav(startAt('roles'));
  }, [initialRole]);

  // ---- intent ----
  const pickIntent = (next: Intent) => {
    setIntent(next);
    setError('');
    if (next === 'professional') {
      go('roles');
    } else {
      setAccountKind('individual');
      setDeclaredRoles([]);
      go('account');
    }
  };

  // "Real-estate marketer" — a fixed BROKER role; the user only picks individual
  // vs company, then goes straight into the account/company step (skipping the
  // full role checklist).
  const startMarketer = (kind: AccountKind) => {
    setError('');
    setIntent('professional');
    setDeclaredRoles(['BROKER']);
    setAccountKind(kind);
    setMarketerOpen(false);
    go(kind === 'company' ? 'company' : 'account');
  };

  // "Real-estate developer" — a company-only DEVELOPER role.
  const startDeveloper = () => {
    setError('');
    setIntent('professional');
    setDeclaredRoles(['DEVELOPER']);
    setAccountKind('company');
    go('company');
  };

  // ---- roles ----
  const toggleRole = (type: ActivityType) => {
    setDeclaredRoles((prev) => (prev.includes(type) ? prev.filter((r) => r !== type) : [...prev, type]));
  };
  const switchKind = (kind: AccountKind) => {
    setAccountKind(kind);
    setDeclaredRoles([]); // role set differs per kind
  };

  // ---- signup ----
  const handleSignUp = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const isPro = intent === 'professional';
      const isOwner = intent === 'own';
      const roles = isPro ? declaredRoles : [];
      // Individual broker keeps a BROKER role+Agent (so the broker workspace +
      // Agent-scoped Nafath work). Everything else registers as a plain USER and
      // becomes a professional via AccountActivity rows post-verify.
      const accountType = isPro && accountKind === 'individual' && roles.includes('BROKER') ? 'broker' : 'individual';
      await signUp(account.email, account.password, {
        firstName: account.firstName,
        lastName: account.lastName,
        phoneNumber: account.phone,
        accountType,
        gender: account.gender,
        nationality: account.nationality,
        birthYear: account.birthYear,
        accountKind: isPro ? accountKind : 'individual',
        declaredRoles: roles,
        isPropertyOwner: isOwner,
      });
      go('verify');
    } catch (err) {
      setError(err instanceof Error ? err.message : tt('Registration failed. Please try again.', 'فشل التسجيل. حاول مرة أخرى.'));
    } finally {
      setBusy(false);
    }
  }, [intent, declaredRoles, accountKind, account, signUp, go]);

  // ---- verify ----
  const handleVerify = useCallback(async () => {
    if (otp.length !== 6) return;
    setBusy(true);
    setError('');
    try {
      const session = await verifyRegistration(account.email, otp);
      activateSession(session);
      setPendingSession(session);
      if (intent === 'professional') {
        go('pro');
      } else {
        // Replace, don't push: the sign-up form must not sit in the Back stack
        // behind the welcome screen (Back from it should leave sign-up).
        navigate(`/register/success?type=${intent === 'own' ? 'owner' : 'individual'}`, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : tt('Invalid or expired code.', 'رمز غير صحيح أو منتهٍ.'));
    } finally {
      setBusy(false);
    }
  }, [otp, account.email, verifyRegistration, activateSession, intent, navigate, go]);

  // ---- pro onboarding ----
  const finishPro = useCallback(async () => {
    if (!pendingSession?.token) return;
    setBusy(true);
    setError('');
    const token = pendingSession.token;
    try {
      // 1) Company kind → create the owned company (promotes USER→COMPANY_ADMIN).
      if (accountKind === 'company') {
        try {
          await apiRequest('/account/company', {
            method: 'POST',
            token,
            body: {
              name: companyName.trim() || `${account.firstName} ${account.lastName}`.trim(),
              phone: companyPhone || account.phone,
              email: companyEmail || account.email,
              licenseNumber: companyCr || undefined,
            },
          });
        } catch (err) {
          // Already created on a previous attempt → continue.
          if (!(err instanceof ApiError && err.status === 409)) throw err;
        }
      }

      const scope = accountKind === 'company' ? 'company' : 'user';
      // 2) Per declared role: create the activity + verify its licence.
      for (const role of declaredRoles) {
        let activityId: string | null = null;
        try {
          const res = await apiRequest<{ activity: { id: string } }>('/account/activities', {
            method: 'POST',
            token,
            body: { activityType: role, scope },
          });
          activityId = res?.activity?.id ?? null;
        } catch (err) {
          // DUPLICATE → look up the existing activity so we can still verify it.
          if (err instanceof ApiError && err.status === 409) {
            try {
              const list = await apiRequest<{ user: Array<{ id: string; activityType: string }>; company: Array<{ id: string; activityType: string }> }>(
                '/account/activities',
                { method: 'GET', token },
              );
              const pool = scope === 'company' ? list.company : list.user;
              activityId = pool?.find((a) => a.activityType === role)?.id ?? null;
            } catch { /* ignore */ }
          } else {
            throw err;
          }
        }
        const license = (licenses[role] || '').trim();
        if (activityId && license) {
          try {
            await apiRequest(`/account/activities/${activityId}/verify-license`, {
              method: 'POST',
              token,
              body: { licenseNumber: license },
            });
          } catch { /* non-fatal: stays VERIFYING, retry from /account/professional */ }
        }
      }

      navigate(accountKind === 'company' ? '/company/dashboard' : '/account/professional', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : tt('Could not finish setup. You can complete it from your account.', 'تعذّر إكمال الإعداد. يمكنك إكماله من حسابك.'));
    } finally {
      setBusy(false);
    }
  }, [pendingSession, accountKind, companyName, companyCr, companyPhone, companyEmail, account, declaredRoles, licenses, navigate]);

  const rolesForCurrentKind = rolesForKind(accountKind);
  const canContinueRoles = declaredRoles.length > 0;

  // The step indicator: the screens already seen, this one, and what the choices
  // so far still lead to (3 steps for a visitor or owner, 4 for an individual
  // marketer, 5 for a company or developer).
  const plan = planFor(nav.history, screen, intent, accountKind);
  const stepLabels: Record<Screen, string> = {
    intent: t('intent.steps.intent', 'Account type'),
    roles: t('intent.steps.roles', 'Activity'),
    company: t('intent.steps.company', 'Company details'),
    account: t('intent.steps.account', 'Your details'),
    verify: t('intent.steps.verify', 'Verify email'),
    pro: t('intent.steps.pro', 'Professional verification'),
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900" dir={dir}>
      <Navigation />
      <main className="max-w-2xl mx-auto px-4 py-10 sm:px-6 sm:py-14">
        <Stepper
          steps={plan.steps.map((id) => ({ id, label: stepLabels[id] }))}
          currentStep={plan.current}
          ariaLabel={t('intent.stepperLabel', 'Sign-up steps')}
          className="mb-8 sm:mb-10"
        />

        {/* ---------------- INTENT ---------------- */}
        {screen === 'intent' && (
          <div>
            <div className="text-center mb-8">
              <h1 className="font-display text-3xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2 text-balance">
                {tt('Create your account', 'أنشئ حسابك')}
              </h1>
              <p className="text-gray-500">{tt('What brings you to Safaqat24?', 'ما الذي تبحث عنه في صفقات 24؟')}</p>
            </div>
            <div className="space-y-3">
              {/* Visitor — a plain browsing account for anyone who just wants to
                  explore, save and enquire on listings. */}
              <button
                type="button"
                onClick={() => pickIntent('visitor')}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-start transition-colors hover:border-[#1B7566] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1B7566]"
              >
                <span className="flex-shrink-0 w-12 h-12 rounded-full bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/10 flex items-center justify-center">
                  <i className="ri-compass-3-line text-2xl text-[#004D40]" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block font-medium text-gray-900 dark:text-white">{tt('Browse as a visitor', 'التصفّح كزائر')}</span>
                  <span className="block text-xs text-gray-400">{tt('Explore, save & enquire on properties', 'تصفّح واحفظ العقارات وتواصل مع المعلنين')}</span>
                </span>
                <i className={`${isAr ? 'ri-arrow-left-line mr-auto' : 'ri-arrow-right-line ml-auto'} text-gray-400`} aria-hidden="true" />
              </button>

              {/* Real-estate marketer — expands to individual/company. */}
              <div>
                <button
                  type="button"
                  aria-expanded={marketerOpen}
                  onClick={() => setMarketerOpen((v) => !v)}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 bg-white dark:bg-gray-800 text-start transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1B7566] ${
                    marketerOpen ? 'border-[#004D40]' : 'border-gray-200 dark:border-gray-700 hover:border-[#1B7566]'
                  }`}
                >
                  <span className="flex-shrink-0 w-12 h-12 rounded-full bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/10 flex items-center justify-center">
                    <i className="ri-key-2-line text-2xl text-[#004D40]" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-medium text-gray-900 dark:text-white">{t('intent.marketer', 'Real-estate marketer')}</span>
                    <span className="block text-xs text-gray-400">{tt('List & market properties', 'أضف وسوّق العقارات')}</span>
                  </span>
                  <i className={`ri-arrow-down-s-line text-xl text-gray-400 transition-transform ${isAr ? 'mr-auto' : 'ml-auto'} ${marketerOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>
                {marketerOpen && (
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    {([
                      { k: 'individual' as AccountKind, icon: 'ri-user-line', en: 'Individual', ar: 'فرد' },
                      { k: 'company' as AccountKind, icon: 'ri-building-line', en: 'Company', ar: 'شركة' },
                    ]).map((o) => (
                      <button
                        key={o.k}
                        type="button"
                        onClick={() => startMarketer(o.k)}
                        className="flex items-center gap-2 justify-center p-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 transition-colors hover:border-[#004D40] hover:bg-[var(--pv-color-surface-sunken)] dark:hover:bg-[#004D40]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1B7566]"
                      >
                        <i className={`${o.icon} text-lg`} aria-hidden="true" />
                        <span className="font-medium text-sm">{tt(o.en, o.ar)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Real-estate developer (company). */}
              <button
                type="button"
                onClick={startDeveloper}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-start transition-colors hover:border-[#1B7566] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1B7566]"
              >
                <span className="flex-shrink-0 w-12 h-12 rounded-full bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/10 flex items-center justify-center">
                  <i className="ri-building-4-line text-2xl text-[#004D40]" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block font-medium text-gray-900 dark:text-white">{tt('Real-estate developer', 'مطوّر عقاري')}</span>
                  <span className="block text-xs text-gray-400">{tt('Market & build projects', 'سوّق وطوّر المشاريع')}</span>
                </span>
                <i className={`${isAr ? 'ri-arrow-left-line mr-auto' : 'ri-arrow-right-line ml-auto'} text-gray-400`} aria-hidden="true" />
              </button>

              {/* Investor — coming soon. */}
              <button
                type="button"
                disabled
                aria-disabled="true"
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-start cursor-not-allowed opacity-75"
              >
                <span className="flex-shrink-0 w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700/40 flex items-center justify-center">
                  <i className="ri-funds-line text-2xl text-gray-400" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block font-medium text-gray-500 dark:text-gray-300">{tt('Investor', 'مستثمر')}</span>
                  <span className="block text-xs text-gray-400">{tt('Fund & back real-estate projects', 'موّل وادعم المشاريع العقارية')}</span>
                </span>
                <span className={`rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2.5 py-0.5 text-xs font-semibold ${isAr ? 'mr-auto' : 'ml-auto'}`}>
                  {tt('Coming soon', 'قريباً')}
                </span>
              </button>
            </div>
            {/* Property-owner CTA — a landlord listing under Safaqat24's licence. */}
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => pickIntent('own')}
                className="text-sm font-medium text-[#004D40] hover:text-[#003930] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1B7566] rounded"
              >
                {tt('I own a property and want to list it (no licence needed) →', 'أملك عقاراً وأريد عرضه (بدون رخصة) ←')}
              </button>
            </div>
            <p className="mt-6 text-center text-sm text-gray-500">
              {tt('Already have an account?', 'لديك حساب؟')}{' '}
              <button type="button" onClick={() => navigate('/signin')} className="font-medium text-[#004D40] hover:underline">
                {tt('Sign in', 'تسجيل الدخول')}
              </button>
            </p>
          </div>
        )}

        {/* ---------------- ROLES (professional) ---------------- */}
        {screen === 'roles' && (
          <div>
            <BackButton onClick={back} isAr={isAr} />
            <div className="text-center mb-6">
              <h1 className="font-display text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2 text-balance">
                {tt('Tell us about your work', 'أخبرنا عن نشاطك')}
              </h1>
              <p className="text-gray-500">{tt('Pick what applies - you can verify the licence(s) right after signup.', 'اختر ما ينطبق عليك - يمكنك توثيق الرخصة بعد التسجيل مباشرة.')}</p>
            </div>

            {/* account kind toggle */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {([
                { k: 'individual' as AccountKind, icon: 'ri-user-line', en: 'Just me (individual)', ar: 'فرد' },
                { k: 'company' as AccountKind, icon: 'ri-building-line', en: 'A company / firm', ar: 'شركة' },
              ]).map((o) => (
                <button
                  key={o.k}
                  type="button"
                  onClick={() => switchKind(o.k)}
                  aria-pressed={accountKind === o.k}
                  className={`flex items-center gap-2 justify-center p-3 rounded-xl border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1B7566] ${
                    accountKind === o.k ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/10 text-[#003930]' : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-[#1B7566]'
                  }`}
                >
                  <i className={`${o.icon} text-lg`} aria-hidden="true" />
                  <span className="font-medium text-sm">{tt(o.en, o.ar)}</span>
                </button>
              ))}
            </div>

            {/* role checklist */}
            <div className="space-y-2.5">
              {rolesForCurrentKind.map((role) => {
                const checked = declaredRoles.includes(role.type);
                return (
                  <label
                    key={role.type}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-colors ${
                      checked ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/10' : 'border-gray-200 dark:border-gray-700 hover:border-[#1B7566]'
                    }`}
                  >
                    <input type="checkbox" checked={checked} onChange={() => toggleRole(role.type)} className="h-5 w-5 rounded accent-[#004D40]" />
                    <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 flex items-center justify-center">
                      <i className={`${role.icon} text-xl text-[#004D40]`} aria-hidden="true" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-medium text-gray-900 dark:text-white">{isAr ? role.labelAr : role.labelEn}</span>
                      {!role.requiresLicense && (
                        <span className="block text-xs text-gray-400">{tt('No licence required', 'لا تتطلب رخصة')}</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <Button variant="outline" onClick={back}>{tt('Back', 'رجوع')}</Button>
              <Button variant="primary" disabled={!canContinueRoles} onClick={() => go(accountKind === 'company' ? 'company' : 'account')}>
                {tt('Continue', 'متابعة')}
              </Button>
            </div>
          </div>
        )}

        {/* ---------------- COMPANY DETAILS (company kind) ---------------- */}
        {screen === 'company' && (
          <div>
            <BackButton onClick={back} isAr={isAr} />
            <div className="text-center mb-6">
              <h1 className="font-display text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2 text-balance">
                {tt('Your company details', 'بيانات شركتك')}
              </h1>
              <p className="text-gray-500">
                {tt("We'll create the company, then add you as its administrator in the next step.", 'سننشئ الشركة ثم نضيفك كمسؤول عنها في الخطوة التالية.')}
              </p>
            </div>
            <div className={`${CARD} max-w-lg mx-auto space-y-4`}>
              <div>
                <label htmlFor="iq-company-name" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  {tt('Company name', 'اسم الشركة')} <span className="text-red-500">*</span>
                </label>
                <Input id="iq-company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder={tt('e.g. Safaqat Real Estate Co.', 'مثال: شركة صفقات العقارية')} />
              </div>
              <div>
                <label htmlFor="iq-company-cr" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  {tt('Commercial Registration (CR) number', 'رقم السجل التجاري')}
                </label>
                <Input id="iq-company-cr" value={companyCr} onChange={(e) => setCompanyCr(e.target.value.replace(/\D/g, ''))} inputMode="numeric" maxLength={10} placeholder={tt('10-digit CR number', 'رقم السجل (10 أرقام)')} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="iq-company-phone" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    {tt('Company phone', 'هاتف الشركة')}
                  </label>
                  <Input id="iq-company-phone" type="tel" inputMode="tel" value={companyPhone} onChange={(e) => setCompanyPhone(e.target.value)} placeholder="+9665XXXXXXXX" />
                </div>
                <div>
                  <label htmlFor="iq-company-email" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    {tt('Company email', 'بريد الشركة')}
                  </label>
                  <Input id="iq-company-email" type="email" value={companyEmail} onChange={(e) => setCompanyEmail(e.target.value)} placeholder="info@company.com" />
                </div>
              </div>
              <p className="text-xs text-gray-400">
                {tt('Your FAAL licence(s) per activity are verified in the final step.', 'يتم توثيق رخص فال لكل نشاط في الخطوة الأخيرة.')}
              </p>
            </div>
            <div className="max-w-lg mx-auto mt-6 flex items-center justify-between gap-3">
              <Button variant="outline" onClick={back}>{tt('Back', 'رجوع')}</Button>
              <Button variant="primary" disabled={companyName.trim().length < 2} onClick={() => go('account')}>
                {tt('Continue', 'متابعة')}
              </Button>
            </div>
          </div>
        )}

        {/* ---------------- ACCOUNT ---------------- */}
        {screen === 'account' && (
          <div>
            <BackButton onClick={back} isAr={isAr} />
            {accountKind === 'company' && intent === 'professional' && (
              <div className="max-w-lg mx-auto mb-5 rounded-xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-sunken)] px-4 py-3 text-center dark:border-[#004D40]/30 dark:bg-[#004D40]/10">
                <p className="text-sm font-semibold text-[#003930]">{tt('Administrator account', 'حساب المسؤول')}</p>
                <p className="mt-0.5 text-xs text-[#002A23]/80">
                  {tt("These are your personal details - you'll be the company's administrator.", 'هذه بياناتك الشخصية - ستكون أنت المسؤول عن الشركة.')}
                </p>
              </div>
            )}
            <AccountInfoForm initialData={account} onChange={setAccount} onValidationChange={setAccountValid} />
            {error && (
              <div className="max-w-lg mx-auto mt-4 p-3 rounded-lg bg-red-50 border border-red-200" role="alert">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <div className="max-w-lg mx-auto mt-6">
              <Button variant="primary" className="w-full" loading={busy} disabled={!accountValid || busy} onClick={handleSignUp}>
                {tt('Continue', 'متابعة')}
              </Button>
            </div>
          </div>
        )}

        {/* ---------------- EMAIL VERIFY ---------------- */}
        {screen === 'verify' && (
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[var(--pv-color-surface-sunken)] flex items-center justify-center">
              <i className="ri-mail-check-line text-3xl text-[#004D40]" aria-hidden="true" />
            </div>
            <h1 className="font-display text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">{tt('Verify your email', 'تأكيد بريدك')}</h1>
            <p className="text-gray-500 mb-6">
              {tt('We sent a 6-digit code to', 'أرسلنا رمزاً من 6 أرقام إلى')} <span className="font-medium text-gray-900 dark:text-white">{account.email}</span>
            </p>
            <div className="flex justify-center mb-4">
              <OtpInput value={otp} onChange={setOtp} disabled={busy} autoFocus />
            </div>
            {error && <p className="text-sm text-red-600 mb-4" role="alert">{error}</p>}
            <Button variant="primary" className="w-full" loading={busy} disabled={otp.length !== 6 || busy} onClick={handleVerify}>
              {tt('Verify', 'تأكيد')}
            </Button>
            <button
              type="button"
              onClick={() => resendRegistrationOtp(account.email).catch(() => {})}
              className="mt-4 text-sm font-medium text-[#004D40] hover:underline"
            >
              {tt('Resend code', 'إعادة إرسال الرمز')}
            </button>
          </div>
        )}

        {/* ---------------- PRO ONBOARDING ---------------- */}
        {screen === 'pro' && (
          <div>
            <div className="text-center mb-6">
              <h1 className="font-display text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2 text-balance">
                {tt('Verify your professional access', 'فعّل وصولك المهني')}
              </h1>
              <p className="text-gray-500">
                {tt('Add your licence(s) and verify your identity to unlock your workspace. You can also do this later.', 'أضف رخصتك وتحقق من هويتك لفتح مساحة عملك. يمكنك القيام بذلك لاحقاً أيضاً.')}
              </p>
            </div>

            {/* licence per declared role */}
            <div className="space-y-3 mb-5">
              {declaredRoles.map((role) => (
                <div key={role} className={CARD}>
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-shield-check-line text-[#004D40]" aria-hidden="true" />
                    <span className="font-medium text-gray-900 dark:text-white">{roleLabel(role, accountKind, isAr)}</span>
                  </div>
                  <label htmlFor={`lic-${role}`} className="block text-sm text-gray-600 dark:text-gray-300 mb-1">
                    {roleRequiresLicense(role)
                      ? tt('FAAL licence number', 'رقم رخصة فال')
                      : tt('Licence number (optional)', 'رقم الرخصة (اختياري)')}
                  </label>
                  <Input
                    id={`lic-${role}`}
                    value={licenses[role] || ''}
                    onChange={(e) => setLicenses((prev) => ({ ...prev, [role]: e.target.value }))}
                    placeholder={tt('e.g. 1100012345…', 'مثال: 1100012345…')}
                    inputMode="numeric"
                  />
                </div>
              ))}
            </div>

            {/* identity (Nafath) */}
            <NafathPanel token={pendingSession?.token} isAr={isAr} />

            {error && <p className="text-sm text-red-600 mt-4" role="alert">{error}</p>}

            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => navigate('/account/professional', { replace: true })}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
              >
                {tt('Skip for now', 'تخطٍّ الآن')}
              </button>
              <Button variant="primary" loading={busy} disabled={busy} onClick={finishPro} className="w-full sm:w-auto">
                {tt('Finish & go to my workspace', 'إنهاء والانتقال لمساحة العمل')}
              </Button>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function BackButton({ onClick, isAr }: { onClick: () => void; isAr: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1B7566] rounded"
    >
      <i className={isAr ? 'ri-arrow-right-line' : 'ri-arrow-left-line'} aria-hidden="true" />
      {isAr ? 'رجوع' : 'Back'}
    </button>
  );
}

/**
 * Compact Nafath identity panel — reuses the verify-identity initiate/poll
 * pattern. Optional within onboarding (the user can finish without it). Renders
 * the "Coming Soon" card when the backend has no NAFATH_APP_ID.
 */
function NafathPanel({ token, isAr }: { token?: string; isAr: boolean }) {
  const tt = (en: string, ar: string) => (isAr ? ar : en);
  const [me, setMe] = useState<NafathMeResponse | null>(null);
  const [nationalId, setNationalId] = useState('');
  const [status, setStatus] = useState<NafathStatus>('not_started');
  const [code, setCode] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedRef = useRef(0);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetchNafathMe(token)
      .then((d) => { if (!cancelled) { setMe(d); if (d.nafathVerificationStatus === 'VERIFIED') setStatus('completed'); } })
      .catch(() => {});
    return () => { cancelled = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [token]);

  const stop = () => { if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } };

  const poll = useCallback(async (transId: string) => {
    if (!token) return;
    try {
      const r = await checkNafathStatus(transId, token);
      setStatus(mapApiStatusToNafathStatus(r.status));
      if (r.status !== 'WAITING') { stop(); return; }
      if (Date.now() - startedRef.current >= MAX_POLL_DURATION_MS) { setStatus('expired'); stop(); return; }
      pollRef.current = setTimeout(() => poll(transId), POLL_INTERVAL_MS);
    } catch {
      if (Date.now() - startedRef.current >= MAX_POLL_DURATION_MS) { setStatus('expired'); stop(); return; }
      pollRef.current = setTimeout(() => poll(transId), POLL_INTERVAL_MS);
    }
  }, [token]);

  const start = async () => {
    if (!token || !/^\d{10}$/.test(nationalId)) {
      setErr(tt('National ID must be 10 digits', 'رقم الهوية يجب أن يكون 10 أرقام'));
      return;
    }
    setErr(null);
    setSubmitting(true);
    try {
      const r = await initiateNafath(nationalId, isAr ? 'ar' : 'en', token);
      setCode(r.random);
      setStatus('waiting');
      startedRef.current = Date.now();
      pollRef.current = setTimeout(() => poll(r.transId), POLL_INTERVAL_MS);
    } catch (e) {
      setErr(e instanceof ApiError && e.status === 503
        ? tt('Identity verification is not enabled yet - you can skip this.', 'خدمة التحقق غير مفعلة بعد - يمكنك تخطيها.')
        : (e instanceof Error ? e.message : tt('Something went wrong.', 'حدث خطأ.')));
    } finally {
      setSubmitting(false);
    }
  };

  if (me && !me.available) {
    return <NafathVerification status="not_started" isApiReady={false} />;
  }
  if (status === 'completed') {
    return (
      <div className="rounded-2xl border border-green-200 bg-green-50 p-4 flex items-center gap-3">
        <i className="ri-checkbox-circle-fill text-2xl text-green-500" aria-hidden="true" />
        <span className="font-medium text-green-700">{tt('Identity verified via Nafath', 'تم التحقق من الهوية عبر نفاذ')}</span>
      </div>
    );
  }
  if (status === 'waiting') {
    return <NafathVerification status="waiting" verificationCode={code} isApiReady onCancel={() => { stop(); setStatus('not_started'); setCode(undefined); }} />;
  }
  return (
    <div className={CARD}>
      <div className="flex items-center gap-2 mb-2">
        <i className="ri-fingerprint-line text-[#004D40]" aria-hidden="true" />
        <span className="font-medium text-gray-900 dark:text-white">{tt('Verify your identity (Nafath)', 'تحقق من هويتك (نفاذ)')}</span>
      </div>
      <p className="text-sm text-gray-500 mb-3">{tt('Optional now - required before you publish.', 'اختياري الآن - مطلوب قبل النشر.')}</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={nationalId} onChange={(e) => setNationalId(e.target.value.replace(/\D/g, ''))} maxLength={10} inputMode="numeric" placeholder="1xxxxxxxxx" />
        <Button variant="outline" onClick={start} loading={submitting} disabled={submitting || nationalId.length !== 10}>
          {tt('Verify identity', 'تحقق')}
        </Button>
      </div>
      {err && <p className="text-sm text-red-600 mt-2">{err}</p>}
    </div>
  );
}
